/**
 * Pure rendering decisions for one job row, shared by the Start view's
 * "Letzte Jobs" card and Task 13's Jobs view -- `<JobLine job>`
 * (components/JobLine.tsx) is the one renderer both call, and everything it
 * needs to DECIDE lives here so Task 13 inherits the same rules without a
 * fourth DOM suite (this plan's DOM-test scope is capped at three, and none
 * of them is spent on this file or on JobLine itself).
 *
 * "DAUER" IS publicJob.durationMs, NEVER stats.durationMs (review N-2,
 * api/src/adminRoutes.ts:233-239 in the merged app tree): durationMs is the
 * wall clock for the row's whole life (queued -> finished), which for a
 * background action also covers the waitUntil scheduling gap, while
 * stats.durationMs times only the job BODY. Showing the body's number as
 * "Dauer" would understate how long the button actually looked busy to the
 * person watching it -- exactly the "job shown as finished when it failed"
 * class of bug the controller's standing rule names. Enforced here simply by
 * NEVER accepting a `stats` parameter: `formatDuration` only ever sees the
 * number `job.durationMs` already is.
 */
import { t } from './i18n';
import type { JobStatus, JobsResponse } from './types';
import type { PillKind } from '../components/StatusPill';

/** 5 s, and only while something is actually moving (Task 13, Step 3). */
export const JOBS_POLL_MS = 5000;

/**
 * WHEN THE MONITOR IS ALLOWED TO POLL. Lives here rather than in
 * lib/jobsView.ts because queries.ts's `jobsQueryOptions` is what wires it, and
 * jobsView.ts imports `invalidateMutation` FROM queries.ts -- putting this
 * there would make the two modules import each other. This file is a leaf
 * (i18n and types only), which is exactly why it can be the shared one.
 *
 * Polls ONLY while a row on the current page is `running` or `queued`. A
 * monitor that polls a quiet table forever is a battery and quota cost with no
 * reader.
 *
 * Deliberately reads `jobs` and not `latest`: `latest` is one row per type
 * regardless of age, so a crashed isolate's permanently-'running' row (the
 * condition plan 5a's RUNNING_STALE_AFTER_MS exists for, server-side) would
 * keep this tab polling for the rest of the day. Every row created while this
 * view is open is on the first page of `jobs` by construction.
 */
export function jobsRefetchInterval(data: JobsResponse | undefined): number | false {
  if (data === undefined || !Array.isArray(data.jobs)) return false;
  return data.jobs.some((job) => job.status === 'running' || job.status === 'queued')
    ? JOBS_POLL_MS
    : false;
}

export function jobStatusPill(status: JobStatus): { kind: PillKind; label: string } {
  switch (status) {
    case 'queued': return { kind: 'neutral', label: t('status.job.queued') };
    case 'running': return { kind: 'neutral', label: t('status.job.running') };
    case 'done': return { kind: 'ok', label: t('status.job.done') };
    case 'error': return { kind: 'error', label: t('status.job.error') };
  }
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;

/**
 * `null` covers a job that never finished -- publicJob only sets durationMs
 * once BOTH started_at and finished_at exist, which a still-queued or
 * still-running row (legitimately present in "last job per type") does not.
 */
export function formatDuration(ms: number | null): string {
  if (ms === null) return t('jobLine.duration.unknown');
  if (ms < SECOND_MS) return t('jobLine.duration.subSecond');
  if (ms < MINUTE_MS) {
    const seconds = (ms / SECOND_MS).toFixed(1).replace('.', ',');
    return t('jobLine.duration.seconds', { seconds });
  }
  const totalSeconds = Math.round(ms / SECOND_MS);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return t('jobLine.duration.minutes', { minutes, seconds });
}

const DATE_TIME = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * A stored instant, formatted, AND IT NEVER THROWS. The twin of
 * lib/startView.ts's `formatDateTime`, written out again here rather than
 * imported for a structural reason (startView.ts imports `invalidateMutation`
 * from queries.ts, and queries.ts imports THIS file for `jobsRefetchInterval`)
 * and kept for a behavioural one: `Intl.DateTimeFormat.format` throws
 * RangeError on an invalid Date, and the two values this formats -- a job row's
 * `startedAt` and `stats.postponedUntil` -- are strings no layer of this client
 * validates. One unreadable row must not take the whole monitor down with it,
 * which is exactly the property `parseStoredStats` defends on the server
 * (adminRoutes.ts:214-222). An unparseable value is shown AS STORED: it is
 * evidence, and hiding it behind a dash would hide the fault.
 */
export function formatTimestamp(iso: string | null): string {
  if (iso === null || iso.trim() === '') return t('jobLine.duration.unknown');
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return DATE_TIME.format(new Date(ms));
}

const RELATIVE_UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
  ['second', 1000],
];

/**
 * `Intl.RelativeTimeFormat('de', ...)` produces already-correct German
 * grammar ("vor 5 Minuten", "vor 1 Tag") -- a hand-rolled {{param}} template
 * would need a singular/plural key per unit for no benefit, since this is a
 * built-in doing exactly what it is for. Rendered as a JSX expression, never
 * a literal, so it does not need (and must not fake) a t() key of its own.
 */
export function formatRelativeTime(iso: string | null, nowMs: number): string {
  if (iso === null) return t('jobLine.notFinished');
  const deltaMs = Date.parse(iso) - nowMs;
  const rtf = new Intl.RelativeTimeFormat('de', { numeric: 'auto' });
  for (const [unit, unitMs] of RELATIVE_UNITS) {
    if (Math.abs(deltaMs) >= unitMs || unit === 'second') {
      return rtf.format(Math.round(deltaMs / unitMs), unit);
    }
  }
  // Unreachable: RELATIVE_UNITS' last entry is 'second', whose branch above
  // matches unconditionally (`unit === 'second'`), so the loop always
  // returns. Kept only so the function has a total return type the compiler
  // can see without a non-null assertion.
  return rtf.format(0, 'second');
}

const ERROR_TRUNCATE_LENGTH = 80;

/** The FULL text still belongs in the `title` attribute; only what is shown
 *  inline is shortened, and only past the same fixed length every time. */
export function truncateError(error: string | null, maxLength = ERROR_TRUNCATE_LENGTH): string | null {
  if (error === null) return null;
  if (error.length <= maxLength) return error;
  return `${error.slice(0, maxLength - 1)}…`;
}
